const INITIAL_STATE = "idle";

export function createRealtimeManager({
  createClient,
  getToken,
  createOptions,
  onClientCreated = () => {},
}) {
  let client = null;
  let state = INITIAL_STATE;
  let startingPromise = null;
  let settleStarting = null;
  let connectionGeneration = 0;
  const listeners = new Set();

  function setState(nextState) {
    if (state === nextState) return;
    state = nextState;
    for (const listener of listeners) listener(state);
  }

  function bindConnection(connection, settle, generation) {
    connection?.bind?.("connected", () => {
      if (generation !== connectionGeneration) return;
      setState("connected");
      settle("connected");
    });
    connection?.bind?.("disconnected", () => {
      if (generation !== connectionGeneration) return;
      setState("disconnected");
    });
    connection?.bind?.("unavailable", () => {
      if (generation !== connectionGeneration) return;
      setState("unavailable");
      settle("unavailable");
    });
    connection?.bind?.("error", () => {
      if (generation !== connectionGeneration) return;
      setState("unavailable");
      settle("unavailable");
    });
  }

  function start() {
    const token = getToken();
    if (!token) {
      setState("unauthenticated");
      return Promise.resolve("unauthenticated");
    }
    if (state === "connected" && client) return Promise.resolve("connected");
    if (startingPromise) return startingPromise;
    if (client) return Promise.resolve(state);

    setState("connecting");
    const generation = ++connectionGeneration;
    startingPromise = new Promise((resolve) => {
      let settled = false;
      const settle = (result) => {
        if (settled) return;
        settled = true;
        if (settleStarting === settle) settleStarting = null;
        startingPromise = null;
        resolve(result);
      };
      settleStarting = settle;

      try {
        client = createClient(createOptions(token));
        onClientCreated(client);
        bindConnection(client?.connector?.pusher?.connection, settle, generation);
      } catch (_error) {
        client = null;
        setState("unavailable");
        settle("unavailable");
      }
    });

    return startingPromise;
  }

  function stop() {
    connectionGeneration += 1;
    settleStarting?.("idle");
    client?.disconnect?.();
    client = null;
    setState(INITIAL_STATE);
  }

  async function restartAuthentication() {
    stop();
    return start();
  }

  function subscribeState(listener) {
    listeners.add(listener);
    listener(state);
    return () => listeners.delete(listener);
  }

  return {
    start,
    stop,
    restartAuthentication,
    getClient: () => client,
    getState: () => state,
    subscribeState,
  };
}
